import { Injectable } from '@angular/core';

import { TRANSFER_STATUS } from '../../models/transfer.model';
import { SyncCryptService } from '../../../core/crypt/sync-crypt.service';
import { UserService } from '../../../core/user.service';
import { LoggerService } from '../../../core/logger.service';
import { FileItemService } from '../../../file/file-item.service';
import { ITransferItemChunkQueue, ITransferItemDownload, ITransferItemUpload } from 'src/sync';
import { TransferType } from '../../models/transfer-type';
import { MAX_SIZE_READ_MEMORY } from '../../transfer.constants';

@Injectable({
  providedIn: 'root',
})
export class TransferItemService {
  private transferItemUpload: ITransferItemUpload = {
    _initTime: 0, // timestamp of this transferitem
    type: TransferType.UPLOAD,
    status: TRANSFER_STATUS.STATUS_WAITING,
    filename: '', // decrypted file name
    fileobjdata: null, // the arraybuffer holding the file.
    filedata: null,
    filesize: 0, // file size in bytes
    filedate: 0, // timestamp
    mimetype: '',
    uniqid: '', // string used to identify this transfer item.
    sync_id: 0,
    data_key: [], // Array like version of the data_key
    bytes_sent: 0,

    share_id: 0, //
    share_sequence: 0, //  for links only
    share_key_id: '', //

    chunkamt: 0,
    progress_percent: 0,
    progress_network: 0,
    progress_crypt: 0,
    progress_bytes: 0,
    stats: '',

    share_key: '', // The decrypted version of the share key
    enc_share_key: '', // optional if share_key is provided
    enc_data_key: '',

    sha1_digest: null, // new sjcl.hash.sha1()
    enc_name: '', // meta encrypted file name
    sync_pid: 0, /// pid for the sync id
    fileobj: null,
    blob_id: 0,
    servtime: 0,
    backup_id: '',
    device_id: 0,
    device_sig_b64: '',
    chunkqueue: [],
  };

  private transferItemDownload: ITransferItemDownload = {
    _initTime: 0, // timestamp of this transferitem
    type: TransferType.DOWNLOAD,
    status: TRANSFER_STATUS.STATUS_WAITING,
    filename: '', // decrypted file name
    filesize: 0, // file size in bytes
    filedate: 0, // timestamp
    mimetype: '',
    uniqid: '', // string used to identify this transfer item.

    sync_id: 0,

    // keys and encryption.
    data_key: [], // Array like version of the data_key
    enc_data_key: '',
    share_key: '',
    enc_share_key: '',
    bytes_sent: 0,
    progress_percent: 0,
    progress_network: 0,
    progress_crypt: 0,
    progress_bytes: 0,

    blobtype: '',
    cachekey: '', // download only.
    dl_ready: false,
    dl_href: '',
    dl_method: '',
    renderFile: undefined,
    enc_filesize: 0,
    filedata: undefined, // hash of file attributes derived from the file name.
  };
  constructor(
    private crypt: SyncCryptService,
    private log: LoggerService,
    private userService: UserService,
    private fileItemService: FileItemService,
  ) {}

  public getChunkQueue(tItem: ITransferItemUpload): ITransferItemChunkQueue[] {
    // this is a file upload.
    const chunkamt = Math.ceil(tItem.filesize / this.crypt.GCM_PAYLOAD_SIZE);
    const queue = [];
    for (let i = 0; i < chunkamt; i++) {
      const offset = i * this.crypt.GCM_PAYLOAD_SIZE,
        len =
          offset + this.crypt.GCM_PAYLOAD_SIZE > tItem.filesize ? tItem.filesize - offset : this.crypt.GCM_PAYLOAD_SIZE;
      queue.push({
        offset: offset,
        enc_offset: offset + 36 * i,
        chunklen: len,
      });
    }
    return queue;
  }

  /**
   * @ngdoc method
   * @name  get
   * @methodOf sync.service:TransferItem
   * @description
   * Creates a TransferItem object using the default template and merging
   * in the settings object
   * @param  {Object} settings Overrides default template for transferItem.
   * @return {Promise}         Resolved promise with a transfer item
   */
  public async get(
    settings: ITransferItemDownload | ITransferItemUpload,
  ): Promise<ITransferItemDownload | ITransferItemUpload> {
    if (!settings.filename) {
      this.log.error('TransferItem.get() passed empty file name.');
      settings.status = 7201;
    }
    // Some browsers report size 0 for valid files (zip-view / cloud placeholders).
    if (!settings.filesize && settings.type === TransferType.UPLOAD) {
      await this.recoverZeroByteUpload(<ITransferItemUpload>settings);
    }
    if (!settings.filesize) {
      this.log.error('TransferItem.get() passed 0 byte file size.');
      settings.status = 7202;
    }
    settings._initTime = Date.now();
    settings.filedata = this.fileItemService.getFileTypeData(settings.filename);
    if (settings.type == TransferType.UPLOAD) {
      return await this.getUploadItem(<ITransferItemUpload>settings);
    } else {
      return await this.getDownloadItem(<ITransferItemDownload>settings);
    }
  }

  /**
   * Recovers the real size of an upload whose File reports size 0 by reading its
   * bytes (which forces non-materialized files to hydrate) and rebuilding the File.
   * Streamed and capped at MAX_SIZE_READ_MEMORY to avoid OOM; on cap/failure the
   * item is left as-is for the 0-byte guard (status 7202).
   */
  private async recoverZeroByteUpload(settings: ITransferItemUpload): Promise<void> {
    const fileObj = settings.fileobj;
    if (!fileObj || typeof fileObj.stream !== 'function') {
      return;
    }
    try {
      const reader = fileObj.stream().getReader();
      const chunks: Uint8Array[] = [];
      let total = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        total += value.byteLength;
        if (total > MAX_SIZE_READ_MEMORY) {
          await reader.cancel();
          this.log.warn('TransferItem.get() skipped 0 byte recovery: file exceeds in-memory read limit');
          return;
        }
        chunks.push(value);
      }
      if (total > 0) {
        settings.fileobj = new File(chunks, fileObj.name, {
          type: fileObj.type,
          lastModified: settings.filedate,
        });
        settings.filesize = settings.fileobj.size;
      }
    } catch (err) {
      this.log.warn('TransferItem.get() failed to recover 0 byte file size: ' + err);
    }
  }

  private async getUploadItem(settings: ITransferItemUpload): Promise<ITransferItemUpload> {
    let tItem: ITransferItemUpload;
    settings.retryCount = 0;
    settings.chunkqueue = this.getChunkQueue(settings);
    settings.chunkamt = settings.chunkqueue.length;
    settings.data_key = this.crypt.mkDataKey(); // mk new datakey
    if (this.userService.isAuthenticated() && !settings.linkID && !settings.enc_name) {
      try {
        const encName = await this.crypt.filenameEncrypt(settings.filename);
        settings.enc_name = encName;
        tItem = Object.assign({}, this.transferItemUpload, settings);
      } catch (err) {
        settings.status = 2022;
        this.log.error('TransferItem enc file name failed');
        this.log.error(err);
        tItem = Object.assign({}, this.transferItemUpload, settings);
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
      }
    } else {
      tItem = Object.assign({}, this.transferItemUpload, settings);
    }
    return tItem;
  }

  private async getDownloadItem(settings: ITransferItemDownload): Promise<ITransferItemDownload> {
    settings.enc_filesize =
      Math.ceil(settings.filesize / this.crypt.GCM_PAYLOAD_SIZE) * this.crypt.GCM_TAG_OVERHEAD + settings.filesize;
    settings.uniqid = this.crypt.getRandomHex(32);

    if (settings.context == 'applink') {
      const tItem = Object.assign({}, this.transferItemDownload, settings);
      return tItem;
    }

    if (!settings.share_key && settings.enc_share_key) {
      try {
        settings.share_key = await this.crypt.sharekeyDecrypt(settings.enc_share_key, settings.share_key_id);
      } catch (ex) {
        const tItem: ITransferItemDownload = Object.assign({}, this.transferItemDownload, settings);
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
        return tItem;
      }
    }
    let tItem: ITransferItemDownload;
    try {
      const datakey = await this.crypt.datakeyDecrypt(settings.enc_data_key, settings.share_key);
      settings.data_key = this.crypt.b64ToBytes(datakey);
      tItem = Object.assign({}, this.transferItemDownload, settings);
    } catch (err) {
      this.log.error(err);
      this.log.error('datakey decrypt failed for TransferItem');
      tItem = Object.assign({}, this.transferItemDownload, settings);
      tItem.status = 2010;
    }
    return tItem;
  }
}
